import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { changePassword, completeNewPassword, setAuthError } from '../store/slices/authSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import MyTextField from '../components/MyTextField';
import MySnackbar from '../components/MySnackbar';
import LoadingComponent from '../components/LoadingComponent';
import s10Logo from '../assets/images/s10Logo.png';
import authImage from '../assets/images/auth.png';

const ChangePassword = () => {
    const theme = useTheme();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const { isLoading, error, challengeName } = useSelector((state: RootState) => state.auth);
    
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [oldPasswordError, setOldPasswordError] = useState('');
    const [newPasswordError, setNewPasswordError] = useState('');
    const [confirmError, setConfirmError] = useState('');

    const isRequired = location.state?.required || challengeName === 'NEW_PASSWORD_REQUIRED';

    useEffect(() => {
        if (!isRequired) {
        }
    }, [isRequired]);

    const validateForm = (): boolean => {
        let isValid = true;

        if (!isRequired) {
            if (!oldPassword) {
                setOldPasswordError('La contraseña actual es requerida');
                isValid = false;
            } else {
                setOldPasswordError('');
            }
        }

        if (!newPassword) {
            setNewPasswordError('La nueva contraseña es requerida');
            isValid = false;
        } else if (newPassword.length < 6) {
            setNewPasswordError('La contraseña debe tener al menos 6 caracteres');
            isValid = false;
        } else {
            setNewPasswordError('');
        }

        if (newPassword !== confirmPassword) {
            setConfirmError('Las contraseñas no coinciden');
            isValid = false;
        } else {
            setConfirmError('');
        }

        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        let result;
        
        if (isRequired) {
            result = await dispatch(completeNewPassword({
                newPassword,
                userAttributes: {}
            }));
            
            if (completeNewPassword.fulfilled.match(result)) {
                navigate('/dashboard');
            }
        } else {
            result = await dispatch(changePassword({
                oldPassword,
                newPassword
            }));
            
            if (changePassword.fulfilled.match(result)) {
                navigate('/dashboard', { state: { message: 'Contraseña cambiada exitosamente' } });
            }
        }
    };

  return (
    <Grid container style={{ minHeight: "100vh", margin: "0 !important" }}>
        <Grid
            container
            item
            xs={12}
            md={6}
            direction="column"
            justifyContent="center"
            alignItems="center"
            style={{
                backgroundColor: theme.s10palette?.S10Blueblue50,
                minHeight: "100vh",
                padding: "2rem 0",
            }}
        >
            <MySnackbar
                open={!!error}
                autoHideDuration={6000}
                msg={error || ''}
                severity="error"
                onClose={() => dispatch(setAuthError(''))}
            />

            <Grid
                container
                direction="row"
                maxWidth="20rem"
                alignItems="center"
                rowSpacing="1rem"
                component="form"
                onSubmit={handleSubmit}
            >
                <Grid justifyContent="center" alignItems="center" alignContent="center" item xs={12} style={{ marginTop: ".5rem" }}>
                    <img style={{ width: "8.75rem" }} src={s10Logo} alt="S10" />
                </Grid>
                <Typography variant="h3" style={{ marginBottom: "0.5rem" }}>
                    ¡Bienvenido!
                </Typography>
                <Typography variant="h6">Portal Mis Boletas - Cambiar contraseña</Typography>
                <Divider
                    style={{
                        width: "100%",
                        marginTop: "1.19rem",
                        marginBottom: "1.5rem",
                        color: theme.s10palette.S10Blueblue100,
                    }}
                />

                <Typography variant="body1" align="left" width="100%">
                    {isRequired
                        ? 'Por seguridad, debes cambiar tu contraseña antes de continuar'
                        : 'Ingresa tu contraseña actual y la nueva contraseña'}
                </Typography>

                {!isRequired && (
                    <MyTextField
                        id="oldPassword"
                        label="Contraseña actual"
                        type={showOldPassword ? 'text' : 'password'}
                        value={oldPassword}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOldPassword(e.target.value)}
                        error={!!oldPasswordError}
                        helperText={oldPasswordError}
                        fullWidth
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowOldPassword(!showOldPassword)}>
                                        {showOldPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                )}

                <MyTextField
                    id="newPassword"
                    label="Nueva contraseña"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                    error={!!newPasswordError}
                    helperText={newPasswordError}
                    fullWidth
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowNewPassword(!showNewPassword)}>
                                    {showNewPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <MyTextField
                    id="confirmPassword"
                    label="Confirmar nueva contraseña"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                    error={!!confirmError}
                    helperText={confirmError}
                    fullWidth
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <Grid container width="20rem" spacing={2} style={{ marginTop: "1rem" }}>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => navigate(-1)}
                        >
                            Cancelar
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            type="submit"
                            disabled={isLoading}
                        >
                            {isRequired ? 'Continuar' : 'Cambiar'}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>

        <Grid
            item
            md={6}
            sx={{ display: { xs: 'none', md: 'block' } }}
            style={{
                backgroundColor: theme.s10palette?.S10Blueblue700,
                backgroundImage: `url(loginDefaultImage.svg)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 0,
                }}
            >
                <img
                    src={authImage}
                    alt="Ilustración auth"
                    style={{
                        maxWidth: '100%',
                        maxHeight: '90%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                    }}
                />
            </Box>
        </Grid>

        {isLoading && <LoadingComponent loadingText="Procesando..." />}
    </Grid>
  );
};

export default ChangePassword;